import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { toast } from "sonner";
import {
    Table2, Save, Loader2, X, Sparkles, Check, Pencil,
    Ruler, GripVertical, Lock,
    Grid3x3, Rows3, Palette, Layers, Type,
} from "lucide-react";
import { PageHeader, PrimaryButton, Card, CardHeader } from "@/components/design-system";
import { WIDGET_TYPO_CLS } from "@/lib/sayz-widget-tokens";
import { SayzCta } from "@/components/widget-cta/SayzCta";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import {
    DndContext, closestCenter, PointerSensor, useSensor, useSensors,
} from "@dnd-kit/core";
import {
    SortableContext, useSortable, verticalListSortingStrategy, arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { fetchAllWidgetSettings, updateWidgetSettings } from "@/lib/widget-settings-api";
import { useSizing } from "@/lib/sizing-store";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
/** Public image serve URL — same pattern as ImagesLibrary. */
const imageSrc = (id, version) => `${BACKEND}/api/embed/images/${id}?v=${version || 1}`;

/** Resolve the final <img src> for a body image.
 *  - Live widget path: backend injects an absolute `url` field — use it as-is.
 *  - Admin panel path: ImagesLibrary objects only carry `id` + version — build
 *    the URL via `imageSrc()` (uses REACT_APP_BACKEND_URL). */
const resolveBodyImageSrc = (bodyImage) => {
    if (!bodyImage) return null;
    if (typeof bodyImage.url === "string" && bodyImage.url) return bodyImage.url;
    if (bodyImage.id) return imageSrc(bodyImage.id, bodyImage.version || bodyImage.current_version);
    return null;
};

/** Find the merchant's "body size" reference image from the ImagesLibrary.
 *  Priority: name/alt includes "body" or "beden" → any first image → null. */
const pickBodySizeImage = (images) => {
    if (!Array.isArray(images) || images.length === 0) return null;
    const norm = (s) => (s || "").toString().toLowerCase();
    const bodyMatch = images.find((img) => {
        const n = norm(img.name);
        const a = norm(img.alt);
        return n.includes("body") || n.includes("beden") || a.includes("body") || a.includes("beden");
    });
    return bodyMatch || images[0];
};

/**
 * Beden Tablosu Modalı — Ürün detay sayfasında Sayz Widget'ta belirlenen
 * "Beden Tablosu" CTA'sına tıklandığında açılacak deneyimi yönetiriz.
 *
 * Sayfa yapısı 2 tab: Görünüm & Yerleşim + İçerik. Sayz Widget ile tam
 * UI paritesi — icon + bilgi paragrafı + count badge deseni.
 */

/* Not: `SAYZ_MODAL_W` / `SAYZ_DRAWER_W` gibi sabit genişlik değerleri
 * müşteri sitesinde gerçek modal/drawer için widget-sizing-panel.js'te
 * tanımlanır. Preview alanında sabit genişlik uygulanmaz — modal/drawer
 * preview kapsayıcısının kendi genişliğince (`w-full`) render edilir. */

const STORAGE_KEY = "sayz.experience.size-chart.v6";

const BLOCK_DEFS = {
    image:            { id: "image",            label: "Görsel",              desc: "Modal üstünde ürün / ölçü diyagramı" },
    table:            { id: "table",            label: "Beden Tablosu",       desc: "Standart tablo veya sekmeli görünüm", locked: true, sortable: false },
    "how-to-measure": { id: "how-to-measure",   label: "Nasıl Ölçülür",       desc: "Ölçü alma rehberi" },
    "cross-cta":      { id: "cross-cta",        label: "Bedenimi Bul köprüsü", desc: "Modal altına ince yönlendirme linki", sortable: false },
};

// Blok sırası sabiti: Beden Tablosu en üstte, Bedenimi Bul köprüsü en altta
// (ikisi de locked/sortable:false). Görsel ↔ Nasıl Ölçülür ikisi arasında
// kullanıcı DnD ile yer değiştirebilir.
const DEFAULT_ORDER = ["table", "image", "how-to-measure", "cross-cta"];
const DEFAULT_CROSS_CTA_TEXT = "Bedenini bulamadın mı?";
const DEFAULT_CROSS_CTA_DESC = "Kısa bir quiz ile sana en uygun bedeni önerelim.";
const DEFAULT_FIND_MY_SIZE_CTA = "Bedenimi Bul";

const DEFAULTS = {
    displayType: "table",
    showTableName: false,        // Beden tablosunun üstünde tabloya verilen ismi göster
    imageEnabled: true,
    tableEnabled: true,
    howToMeasureEnabled: true,
    crossCtaEnabled: true,
    crossCtaText: DEFAULT_CROSS_CTA_TEXT,
    crossCtaDesc: DEFAULT_CROSS_CTA_DESC,
    blockOrder: DEFAULT_ORDER,
};

/* Modal is the only supported opening style — the widget opens as a centered
 * popup with a fade animation. Legacy drawer config (openingStyle,
 * drawerDesktop, drawerMobile) is silently dropped on hydration below. */

const SAMPLE = {
    productImageBg: "linear-gradient(135deg, #f4e6d9 0%, #e6d3c1 100%)",
    name: "Kadın Bluz · Genel Beden Tablosu",
    sizes: ["XS", "S", "M", "L", "XL"],
    rows: [
        { label: "Göğüs", values: [86, 90, 94, 98, 102] },
        { label: "Bel",   values: [62, 66, 70, 74, 78] },
        { label: "Kalça", values: [90, 94, 98, 102, 106] },
    ],
};

const DEFAULT_MODAL_TITLE = "Ölçülere Bak";

/**
 * Merge partial size-chart settings into DEFAULTS, cleaning blockOrder.
 * Reusable by both localStorage seed and backend hydration. Legacy drawer
 * keys from pre-MVP persisted docs are silently stripped.
 */
function mergeSettings(base, patch) {
    if (!patch || typeof patch !== "object") return base;
    // Blok sırasını kurallara göre normalize et:
    //   1. table → her zaman pozisyon 0 (locked)
    //   2. cross-cta → her zaman son pozisyon (locked)
    //   3. image / how-to-measure → kullanıcının kaydettiği sırayı koru
    //      (yoksa DEFAULT_ORDER'daki sırayla doldur)
    const incoming = Array.isArray(patch.blockOrder) ? patch.blockOrder : [];
    const sortableIncoming = incoming.filter((k) => k === "image" || k === "how-to-measure");
    const sortableFallback = DEFAULT_ORDER.filter((k) => (k === "image" || k === "how-to-measure") && !sortableIncoming.includes(k));
    const middle = [...sortableIncoming, ...sortableFallback];
    const cleanOrder = ["table", ...middle, "cross-cta"];
    // Strip legacy drawer-related keys that may still exist in persisted docs.
    const { openingStyle: _os, drawerDesktop: _dd, drawerMobile: _dm, ...sanitized } = patch;
    return { ...base, ...sanitized, blockOrder: cleanOrder, tableEnabled: true };
}

/**
 * Merge chart settings into the sizing_panel blocks array under the
 * size_table block's content.chart field. If the block is missing,
 * append it with defaults so the panel keeps rendering it.
 */
function mergeChartIntoBlocks(blocks, chart) {
    const hasSt = blocks.some((b) => b.key === "size_table");
    if (!hasSt) {
        return [
            ...blocks,
            {
                key: "size_table",
                enabled: true,
                order: blocks.length,
                content: {
                    title: "Beden Tablosu",
                    description: "Ölçüleri tablodan karşılaştırın.",
                    buttonText: "Ölçülere Bak",
                    defaultUnit: "cm",
                    chart,
                },
            },
        ];
    }
    return blocks.map((b) => {
        if (b.key !== "size_table") return b;
        return { ...b, content: { ...(b.content || {}), chart } };
    });
}

/* Sayz Widget ile birebir tab styling */
const TAB_LIST_CLS =
    "flex w-full items-center bg-white rounded-lg p-1 h-10 gap-1";
const TAB_TRIGGER_CLS =
    "group relative flex-1 inline-flex items-center justify-center gap-2 h-8 px-3 rounded-lg text-[12.5px] font-medium transition-all border border-transparent " +
    "text-muted-foreground hover:text-foreground data-[state=inactive]:hover:bg-muted/60 " +
    "data-[state=active]:bg-foreground data-[state=active]:border-foreground data-[state=active]:text-background data-[state=active]:shadow-sm " +
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foreground/20 focus-visible:ring-offset-0";
const TAB_BADGE_CLS =
    "ml-0.5 inline-flex items-center justify-center min-w-[16px] h-[16px] px-1 rounded-full bg-muted text-[10px] font-semibold text-foreground/80 leading-none transition-colors " +
    "group-data-[state=active]:bg-background/20 group-data-[state=active]:text-background";

export default function SizeChartModal() {
    const [saving, setSaving] = useState(false);
    const [loading, setLoading] = useState(true);
    const [settings, setSettings] = useState(DEFAULTS);
    const [accent, setAccent] = useState("#C86B4B");
    const [modalTitle, setModalTitle] = useState(DEFAULT_MODAL_TITLE);
    const [findMySizeCta, setFindMySizeCta] = useState(DEFAULT_FIND_MY_SIZE_CTA);
    const [tab, setTab] = useState("appearance");
    // Widget seviyesi tasarım tokenları — CrossCta gibi widget item'larının
    // (Bedenimi Bul CTA'sı vb.) merkezî tasarım kaynağı Sayz Widget sayfası.
    // Merchant orada radius/spacing/accent değiştirdiğinde buradaki popup
    // önizlemesindeki CTA da otomatik güncellenir.
    const [widgetTemplate, setWidgetTemplate] = useState("minimal");
    const [widgetTokens, setWidgetTokens] = useState({ accent: "#C86B4B" });

    // Görseller sayfasından body size görselini otomatik seç.
    const { images, groups } = useSizing();
    const bodyImage = useMemo(() => pickBodySizeImage(images), [images]);
    // Önizleme için gerçek ölçü grubu verilerinden bir örnek çek:
    // en çok "Nasıl Ölçülür" açıklaması dolu olan grubu tercih et.
    const previewGuides = useMemo(() => {
        const groupsWithHtm = (groups || [])
            .map((g) => {
                const filled = (g.measures || []).filter(
                    (m) => m && typeof m === "object" && m.howToMeasure
                );
                return { group: g, filled };
            })
            .filter((x) => x.filled.length > 0)
            .sort((a, b) => b.filled.length - a.filled.length);
        if (!groupsWithHtm.length) return null;
        return groupsWithHtm[0].filled.map((m) => ({
            key: m.kind || m.name,
            label: m.name,
            hint: m.howToMeasure,
        }));
    }, [groups]);

    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const data = await fetchAllWidgetSettings();
                if (!alive) return;
                const c = data?.sizing_panel?.config || {};
                if (c?.tokens?.accent) setAccent(c.tokens.accent);
                if (c?.template) setWidgetTemplate(c.template);
                if (c?.tokens) setWidgetTokens((prev) => ({ ...prev, ...c.tokens }));
                if (Array.isArray(c.blocks)) {
                    const st = c.blocks.find((b) => b.key === "size_table");
                    const btn = st?.content?.buttonText?.trim();
                    if (btn) setModalTitle(btn);
                    const fms = c.blocks.find((b) => b.key === "find_my_size");
                    const fmsBtn = fms?.content?.buttonText?.trim();
                    if (fmsBtn) setFindMySizeCta(fmsBtn);
                    // Hydrate chart settings from block.content.chart (source of truth).
                    // Backward-compat: if not present, seed once from legacy localStorage.
                    const remoteChart = st?.content?.chart;
                    if (remoteChart && typeof remoteChart === "object") {
                        setSettings((prev) => mergeSettings(prev, remoteChart));
                    } else {
                        try {
                            const saved = localStorage.getItem(STORAGE_KEY);
                            if (saved) {
                                const parsed = JSON.parse(saved);
                                setSettings((prev) => mergeSettings(prev, parsed));
                            }
                        } catch { /* ignore */ }
                    }
                }
            } catch {
                if (alive) toast.error("Beden Tablosu ayarları yüklenemedi");
            } finally {
                if (alive) setLoading(false);
            }
        })();
        return () => { alive = false; };
    }, []);

    const update = (patch) => setSettings((s) => ({ ...s, ...patch }));

    const handleSave = async () => {
        setSaving(true);
        try {
            // Read-mutate-write: fetch latest panel config, merge chart into
            // size_table block, PUT full config back. Prevents overwriting
            // panel-level settings edited on the SayzWidget page.
            const data = await fetchAllWidgetSettings();
            const w = data?.sizing_panel || {};
            const currentConfig = w.config || {};
            const currentBlocks = Array.isArray(currentConfig.blocks) ? currentConfig.blocks : [];
            const nextConfig = {
                ...currentConfig,
                blocks: mergeChartIntoBlocks(currentBlocks, settings),
            };
            await updateWidgetSettings("sizing_panel", { config: nextConfig });
            try { localStorage.setItem(STORAGE_KEY, JSON.stringify(settings)); } catch { /* ignore */ }
            toast.success("Beden Tablosu ayarları kaydedildi");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Kaydedilemedi");
        } finally {
            setSaving(false);
        }
    };

    // Aktif blok sayısı (İçerik tab badge)
    const activeBlockCount = useMemo(() => {
        return settings.blockOrder.filter((id) => {
            const def = BLOCK_DEFS[id];
            if (!def) return false;
            if (def.locked) return true;
            const k = enabledKey[id];
            return !!settings[k];
        }).length;
    }, [settings]);

    return (
        <div className="space-y-6" data-testid="size-chart-modal-page">
            <PageHeader
                icon={Table2}
                title="Beden Tablosu"
                description="Müşteri Sayz Widget'ta belirlediğiniz CTA'ya tıkladığında açılacak modalın/drawer'ın davranışını ve içeriğini yönetin. Tema ve renk widget'tan otomatik miras alınır."
                actions={
                    <PrimaryButton onClick={handleSave} disabled={saving || loading} data-testid="size-chart-modal-save-btn">
                        {saving ? (
                            <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Kaydediliyor</>
                        ) : (
                            <><Save className="h-3.5 w-3.5" /> Kaydet</>
                        )}
                    </PrimaryButton>
                }
            />

            {loading ? (
                <div className="flex items-center justify-center py-24 text-muted-foreground" data-testid="sc-loading">
                    <Loader2 className="h-4 w-4 animate-spin mr-2" /> Yükleniyor…
                </div>
            ) : (
            <Tabs value={tab} onValueChange={setTab} data-testid="sc-tabs">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
                    {/* SOL — Tablı ayar paneli (Sayz Widget birebir) */}
                    <div className="lg:col-span-7">
                        <div className="rounded-xl border border-border bg-background overflow-hidden">
                            <div className="px-5 pt-5 pb-3 border-b border-border bg-white">
                                <TabsList className={TAB_LIST_CLS}>
                                    <TabsTrigger value="appearance" className={TAB_TRIGGER_CLS} data-testid="sc-tab-appearance">
                                        <Palette className="h-3.5 w-3.5" strokeWidth={1.75} />
                                        <span>Görünüm &amp; Yerleşim</span>
                                    </TabsTrigger>
                                    <TabsTrigger value="content" className={TAB_TRIGGER_CLS} data-testid="sc-tab-content">
                                        <Layers className="h-3.5 w-3.5" strokeWidth={1.75} />
                                        <span>İçerik</span>
                                        <span className={TAB_BADGE_CLS} data-testid="sc-tab-content-badge">
                                            {activeBlockCount}
                                        </span>
                                    </TabsTrigger>
                                </TabsList>
                            </div>

                            <div className="p-4 space-y-4">
                                <TabsContent value="appearance" className="mt-0 space-y-4">
                                    <p className="text-[11px] text-muted-foreground leading-relaxed px-1" data-testid="sc-tab-info-appearance">
                                        Modalın hangi görüntüleme tipiyle sunulduğunu buradan yönet.
                                        Değişikliklerin sağdaki önizlemede anlık yansır.
                                    </p>
                                    <DisplayTypeCard settings={settings} onChange={update} />
                                    <TableSettingsCard settings={settings} onChange={update} />
                                </TabsContent>

                                <TabsContent value="content" className="mt-0 space-y-4">
                                    <p className="text-[11px] text-muted-foreground leading-relaxed px-1" data-testid="sc-tab-info-content">
                                        Modalda hangi bilgiler görünsün, hangi sırayla yerleşsin buradan yönet.
                                        Sürükleyerek sıralayabilir, ihtiyaç dışı blokları kapatabilirsin.
                                    </p>
                                    <ContentBlocksCard settings={settings} onChange={update} />
                                </TabsContent>
                            </div>
                        </div>
                    </div>

                    {/* SAĞ — Sticky Preview */}
                    <div className="lg:col-span-5">
                        <div className="sticky top-20">
                            <Card>
                                <CardHeader
                                    title="Önizleme"
                                    desc="Ziyaretçi bu şekilde görecek."
                                />
                                <div className="p-4 bg-background rounded-b-xl">
                                    <PreviewCanvas
                                        settings={settings}
                                        accent={accent}
                                        sample={SAMPLE}
                                        modalTitle={modalTitle}
                                        findMySizeCta={findMySizeCta}
                                        bodyImage={bodyImage}
                                        previewGuides={previewGuides}
                                        widgetTemplate={widgetTemplate}
                                        widgetTokens={widgetTokens}
                                    />
                                </div>
                            </Card>
                        </div>
                    </div>
                </div>
            </Tabs>
            )}
        </div>
    );
}

/* ═══════════════════════════════════════════════════════════════
 * GÖRÜNTÜLEME TİPİ
 * ═══════════════════════════════════════════════════════════════ */

/* Reusable "pick-one card" tile — used by DisplayTypeCard below. */
const StyleOption = ({ active, onClick, Icon, label, desc, testId }) => (
    <button
        type="button"
        onClick={onClick}
        className={`group rounded-lg border p-3 text-left transition-all ${
            active
                ? "border-foreground bg-muted/40 shadow-sm"
                : "border-border bg-background hover:border-foreground/30"
        }`}
        data-testid={testId}
        aria-pressed={active}
    >
        <div className="flex items-center gap-2.5">
            <div className={`h-8 w-8 shrink-0 rounded-md grid place-items-center transition-colors ${
                active ? "bg-foreground text-background" : "bg-muted text-muted-foreground group-hover:text-foreground"
            }`}>
                <Icon className="h-4 w-4" strokeWidth={1.75} />
            </div>
            <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold truncate">{label}</div>
                <div className="text-[11px] text-muted-foreground mt-0.5 leading-relaxed truncate">{desc}</div>
            </div>
        </div>
    </button>
);

const DisplayTypeCard = ({ settings, onChange }) => {
    const options = [
        { value: "table", label: "Standart Tablo", desc: "Tümü yan yana",     Icon: Grid3x3 },
        { value: "tabs",  label: "Sekmeli",        desc: "Tek beden odaklı", Icon: Rows3 },
    ];
    return (
        <Card>
            <CardHeader title="Görüntüleme Tipi" desc="Ölçüler nasıl gösterilsin?" />
            <div className="p-4 pt-3 grid grid-cols-2 gap-2.5">
                {options.map((o) => (
                    <StyleOption
                        key={o.value}
                        active={settings.displayType === o.value}
                        onClick={() => onChange({ displayType: o.value })}
                        Icon={o.Icon}
                        label={o.label}
                        desc={o.desc}
                        testId={`sc-display-${o.value}`}
                    />
                ))}
            </div>
        </Card>
    );
};

/* ═══════════════════════════════════════════════════════════════
 * AYARLAR — Tablo davranışını yönet
 * ═══════════════════════════════════════════════════════════════ */

const TableSettingsCard = ({ settings, onChange }) => (
    <Card>
        <CardHeader
            title="Ayarlar"
            desc="Beden tablosu ekranında görünen ek unsurları yönet."
        />
        <div className="px-4 py-2 divide-y divide-border/60" data-testid="sc-settings-card">
            <div className="py-3.5" data-testid="sc-toggle-show-table-name-row">
                <div className="flex items-start gap-3">
                    <span className="h-9 w-9 shrink-0 rounded-md bg-muted text-muted-foreground grid place-items-center mt-0.5">
                        <Type className="h-4 w-4" strokeWidth={1.75} />
                    </span>
                    <div className="min-w-0 flex-1">
                        <div className="flex items-center justify-between gap-3">
                            <div className="text-[13px] font-semibold text-foreground">Tablo Başlığı</div>
                            <Switch
                                checked={settings.showTableName === true}
                                onCheckedChange={(v) => onChange({ showTableName: v })}
                                data-testid="sc-toggle-show-table-name"
                            />
                        </div>
                        <div className="text-[11.5px] text-muted-foreground mt-1 leading-snug">
                            Beden tablosunun hemen üstünde, tabloya verdiğin adı ziyaretçiye göster.
                        </div>
                        <div className="text-[10.5px] text-muted-foreground/70 mt-1 leading-snug italic">
                            Örn: &quot;Kadın Bluz · Genel Beden Tablosu&quot;. Ürüne bağlı tablonun adı otomatik gelir.
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </Card>
);

/* ═══════════════════════════════════════════════════════════════
 * İÇERİK — D&D bloklar
 * ═══════════════════════════════════════════════════════════════ */

const enabledKey = {
    image: "imageEnabled",
    table: "tableEnabled",
    "how-to-measure": "howToMeasureEnabled",
    "cross-cta": "crossCtaEnabled",
};

const isSortableBlock = (id) => BLOCK_DEFS[id]?.sortable !== false;

const ContentBlocksCard = ({ settings, onChange }) => {
    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));

    const handleDragEnd = (event) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;
        // Yalnızca sortable olanlar yer değiştirebilir; table ve cross-cta
        // pozisyonları sabit kalır. Önce sortable id'leri izole edip
        // kendi aralarında arrayMove, sonra full blockOrder'ı yürüyerek
        // yeniden kuruyoruz — böylece sabit blokların indexleri korunur.
        if (!isSortableBlock(active.id) || !isSortableBlock(over.id)) return;
        const sortableIds = settings.blockOrder.filter(isSortableBlock);
        const oldIdx = sortableIds.indexOf(active.id);
        const newIdx = sortableIds.indexOf(over.id);
        if (oldIdx < 0 || newIdx < 0) return;
        const moved = arrayMove(sortableIds, oldIdx, newIdx);
        let i = 0;
        const newOrder = settings.blockOrder.map((id) =>
            isSortableBlock(id) ? moved[i++] : id,
        );
        onChange({ blockOrder: newOrder });
    };

    return (
        <Card>
            <CardHeader
                title="Modal İçeriği"
                desc="Görsel ve Nasıl Ölçülür bloklarını sürükleyerek kendi aralarında yer değiştirebilirsin. Beden Tablosu ve Bedenimi Bul köprüsünün konumu sabittir."
            />
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <SortableContext items={settings.blockOrder.filter(isSortableBlock)} strategy={verticalListSortingStrategy}>
                    <div className="divide-y divide-border">
                        {settings.blockOrder.map((blockId) => {
                            const def = BLOCK_DEFS[blockId];
                            if (!def) return null;
                            const key = enabledKey[blockId];
                            const checked = def.locked ? true : !!settings[key];
                            const sortable = isSortableBlock(blockId);
                            return (
                                <BlockRow
                                    key={blockId}
                                    id={blockId}
                                    def={def}
                                    checked={checked}
                                    sortable={sortable}
                                    settings={settings}
                                    onToggle={(v) => !def.locked && onChange({ [key]: v })}
                                    onCtaTextChange={(v) => onChange({ crossCtaText: v })}
                                    onCtaDescChange={(v) => onChange({ crossCtaDesc: v })}
                                />
                            );
                        })}
                    </div>
                </SortableContext>
            </DndContext>
        </Card>
    );
};

const BlockRow = ({ id, def, checked, sortable = true, settings, onToggle, onCtaTextChange, onCtaDescChange }) => {
    // `disabled: !sortable` → table ve cross-cta satırları sürükleme listeners'ı
    // almaz; collision detection onları atlar. Grid alignment aynı kalsın
    // diye drag handle slot'una ikon yerine spacer bırakıyoruz.
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id, disabled: !sortable });
    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        zIndex: isDragging ? 10 : "auto",
    };

    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(settings.crossCtaText || DEFAULT_CROSS_CTA_TEXT);
    const startEdit = () => { setDraft(settings.crossCtaText || DEFAULT_CROSS_CTA_TEXT); setEditing(true); };
    const commitEdit = () => {
        const t = draft.trim();
        if (t) onCtaTextChange(t);
        setEditing(false);
    };
    const cancelEdit = () => setEditing(false);

    const [editingDesc, setEditingDesc] = useState(false);
    const [descDraft, setDescDraft] = useState(settings.crossCtaDesc || DEFAULT_CROSS_CTA_DESC);
    const startEditDesc = () => { setDescDraft(settings.crossCtaDesc || DEFAULT_CROSS_CTA_DESC); setEditingDesc(true); };
    const commitEditDesc = () => {
        const t = descDraft.trim();
        if (t) onCtaDescChange(t);
        setEditingDesc(false);
    };
    const cancelEditDesc = () => setEditingDesc(false);

    const isCrossCta = id === "cross-cta";
    const editableLabel = isCrossCta ? (settings.crossCtaText || DEFAULT_CROSS_CTA_TEXT) : def.label;
    const editableDesc = isCrossCta ? (settings.crossCtaDesc || DEFAULT_CROSS_CTA_DESC) : def.desc;

    return (
        <div
            ref={setNodeRef}
            style={style}
            className="grid items-center gap-2 p-3 hover:bg-muted/30 transition-colors"
            data-testid={`sc-block-${id}`}
        >
            <div className="grid items-center" style={{ gridTemplateColumns: "24px 12px 1fr auto" }}>
                {sortable ? (
                    <button
                        type="button"
                        {...attributes}
                        {...listeners}
                        className="h-full flex items-center justify-center text-muted-foreground/50 hover:text-foreground cursor-grab active:cursor-grabbing touch-none"
                        aria-label="Sürükleyerek sırala"
                        title="Sürükleyerek sırala"
                        data-testid={`sc-block-drag-${id}`}
                    >
                        <GripVertical className="h-3.5 w-3.5" strokeWidth={1.75} />
                    </button>
                ) : (
                    <div
                        aria-hidden="true"
                        className="h-full w-full"
                        data-testid={`sc-block-pinned-${id}`}
                    />
                )}
                <div aria-hidden="true" />
                <div className="min-w-0 pr-3">
                    {editing && isCrossCta ? (
                        <div className="flex items-center gap-2">
                            <Input
                                value={draft}
                                onChange={(e) => setDraft(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") commitEdit();
                                    if (e.key === "Escape") cancelEdit();
                                }}
                                autoFocus
                                className="h-8 text-sm"
                                placeholder={DEFAULT_CROSS_CTA_TEXT}
                                data-testid="sc-block-cta-input"
                            />
                            <button type="button" onClick={commitEdit}
                                className="shrink-0 h-8 w-8 grid place-items-center rounded-md text-foreground hover:bg-muted transition-colors"
                                aria-label="Kaydet" data-testid="sc-block-cta-save">
                                <Check className="h-4 w-4" strokeWidth={2} />
                            </button>
                            <button type="button" onClick={cancelEdit}
                                className="shrink-0 h-8 w-8 grid place-items-center rounded-md text-muted-foreground hover:bg-muted transition-colors"
                                aria-label="İptal" data-testid="sc-block-cta-cancel">
                                <X className="h-4 w-4" strokeWidth={2} />
                            </button>
                        </div>
                    ) : (
                        <>
                            <div className="text-sm font-medium flex items-center gap-1.5">
                                <span className="truncate">{editableLabel}</span>
                                {def.locked && (
                                    <span className="text-[9px] font-semibold uppercase tracking-wider px-1.5 py-[1px] rounded bg-muted text-muted-foreground border border-border shrink-0">
                                        omurga
                                    </span>
                                )}
                                {isCrossCta && (
                                    <button
                                        type="button"
                                        onClick={startEdit}
                                        className="shrink-0 h-6 w-6 grid place-items-center rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                                        aria-label="CTA metnini düzenle"
                                        data-testid="sc-block-cta-edit"
                                    >
                                        <Pencil className="h-3.5 w-3.5" strokeWidth={1.75} />
                                    </button>
                                )}
                            </div>
                            {isCrossCta && editingDesc ? (
                                <div className="flex items-center gap-2 mt-1">
                                    <Input
                                        value={descDraft}
                                        onChange={(e) => setDescDraft(e.target.value)}
                                        onKeyDown={(e) => {
                                            if (e.key === "Enter") commitEditDesc();
                                            if (e.key === "Escape") cancelEditDesc();
                                        }}
                                        autoFocus
                                        className="h-7 text-[11px]"
                                        placeholder={DEFAULT_CROSS_CTA_DESC}
                                        data-testid="sc-block-cta-desc-input"
                                    />
                                    <button type="button" onClick={commitEditDesc}
                                        className="shrink-0 h-7 w-7 grid place-items-center rounded-md text-foreground hover:bg-muted transition-colors"
                                        aria-label="Kaydet" data-testid="sc-block-cta-desc-save">
                                        <Check className="h-3.5 w-3.5" strokeWidth={2} />
                                    </button>
                                    <button type="button" onClick={cancelEditDesc}
                                        className="shrink-0 h-7 w-7 grid place-items-center rounded-md text-muted-foreground hover:bg-muted transition-colors"
                                        aria-label="İptal" data-testid="sc-block-cta-desc-cancel">
                                        <X className="h-3.5 w-3.5" strokeWidth={2} />
                                    </button>
                                </div>
                            ) : (
                                <div className="text-[11px] text-muted-foreground leading-relaxed mt-0.5 flex items-center gap-1.5">
                                    <span className="truncate">{editableDesc}</span>
                                    {isCrossCta && (
                                        <button
                                            type="button"
                                            onClick={startEditDesc}
                                            className="shrink-0 h-5 w-5 grid place-items-center rounded-md text-muted-foreground/70 hover:text-foreground hover:bg-muted transition-colors"
                                            aria-label="CTA açıklamasını düzenle"
                                            data-testid="sc-block-cta-desc-edit"
                                        >
                                            <Pencil className="h-3 w-3" strokeWidth={1.75} />
                                        </button>
                                    )}
                                </div>
                            )}
                        </>
                    )}
                </div>
                {def.locked ? (
                    <div
                        className="h-5 w-9 flex items-center justify-center text-muted-foreground/70"
                        aria-label="Bu blok her zaman açık"
                        title="Beden tablosu her zaman görünür"
                        data-testid={`sc-block-locked-${id}`}
                    >
                        <Lock className="h-3.5 w-3.5" strokeWidth={1.75} />
                    </div>
                ) : (
                    <Switch
                        checked={checked}
                        onCheckedChange={onToggle}
                        data-testid={`sc-block-toggle-${id}`}
                    />
                )}
            </div>
        </div>
    );
};

/* ═══════════════════════════════════════════════════════════════
 * PREVIEW
 * ═══════════════════════════════════════════════════════════════ */

/* Sayz Widget önizleme deseni — modal-only (MVP'de drawer kaldırıldı). */
const PreviewCanvas = ({ settings, accent, sample, modalTitle, findMySizeCta, bodyImage, previewGuides, widgetTemplate, widgetTokens, onClose, onSwitchToFindMySize }) => {
    // Blocks visible per the user's enabled toggles + ordering.
    const visibleBlocks = useMemo(() => {
        return settings.blockOrder.filter((id) => {
            const def = BLOCK_DEFS[id];
            if (!def) return false;
            if (def.locked) return true;
            const k = enabledKey[id];
            return !!settings[k];
        });
    }, [settings]);

    // Split: cross-cta (if enabled) is rendered as a sticky footer so the
    // primary conversion action stays visible regardless of scroll position.
    // All other visible blocks live inside the scrollable body.
    const hasFooterCta = visibleBlocks.includes("cross-cta");
    const inlineBlocks = useMemo(
        () => visibleBlocks.filter((id) => id !== "cross-cta"),
        [visibleBlocks],
    );

    const styleVars = { "--sayz-scroll-accent": accent };
    // Template DNA flags — editorial lüks sans, bold Field Notes chunky.
    const isEditorial = widgetTemplate === "editorial";
    const isBold = widgetTemplate === "bold";

    // Scroll ref kept purely for programmatic focus if ever needed. Edge
    // tracking + chevron hint + fade masks were intentionally removed per
    // design review — the premium scrollbar alone communicates scrollability
    // without crowding the modal with hints/vignettes.
    const scrollRef = useRef(null);

    return (
        <div
            className={`sayz-sc-shell ${(isEditorial || isBold) ? "" : "rounded-lg "}bg-surface border ${isEditorial ? "border-foreground/20" : isBold ? "border-border" : "border-border"} py-5 px-6 flex flex-col sayz-anim-fade overflow-hidden`}
            data-testid="sc-preview-modal"
            data-editorial={isEditorial ? "true" : "false"}
            data-bold={isBold ? "true" : "false"}
            style={{
                ...styleVars,
                // Mobile: parent (LiveEmbed flex column) içinde `flex: 1`
                //   olarak shrink'liyoruz; shell maxHeight (100dvh-32)
                //   içinde signature'a yer bırakarak modal kendini daraltır.
                //   Masaüstü: 760px cap (ferahlık) devrede. `maxHeight: 100%`
                //   mobile'da flex'ten geleni honor eder, 760 cap da
                //   büyük ekranlarda ferahlığı kesmez.
                maxHeight: 760,
                flex: 1,
                minHeight: 0,
                fontWeight: isEditorial ? 300 : isBold ? 450 : undefined,
                borderRadius: (isEditorial || isBold) ? 0 : undefined,
                borderWidth: isEditorial ? 0.5 : isBold ? 0.5 : undefined,
            }}
        >
            {/* Premium scroll styling — scoped to this modal via a nested selector
                so it works both in the admin preview (document CSS) and inside
                the live Shadow DOM (cssText-injected sheet). Ultra-slim 3px
                bar, accent-tinted, invisible at rest, elegant glass-like
                thumb on hover. No chevrons, no fade masks — the scrollbar
                itself is the only affordance, per luxury design review. */}
            <style>{`
                [data-testid="sc-preview-modal"] .sayz-scroll-premium {
                    -webkit-overflow-scrolling: touch;
                    overscroll-behavior: contain;
                    scroll-behavior: smooth;
                    /* Scrollbar görsel olarak zarif olsun ama LAYOUT'u etkilemesin:
                       Firefox'ta tamamen gizlendi (none), Chrome'da webkit custom
                       3px scrollbar overlay olarak çıkıyor. Bu sayede header & footer
                       ile content box birebir aynı x-koordinatında duruyor (px-6'nın
                       tam 24px'i). scrollbar-gutter YOK — gutter 10px rezerv eden
                       davranış içerik hizasını bozuyordu. */
                    scrollbar-width: none;
                    transition: scrollbar-color 320ms ease;
                }
                [data-testid="sc-preview-modal"] .sayz-scroll-premium {
                    -ms-overflow-style: none;
                }
                [data-testid="sc-preview-modal"] .sayz-scroll-premium::-webkit-scrollbar {
                    width: 3px;
                    height: 3px;
                }
                [data-testid="sc-preview-modal"] .sayz-scroll-premium::-webkit-scrollbar-track {
                    background: transparent;
                    margin-block: 10px;
                }
                [data-testid="sc-preview-modal"] .sayz-scroll-premium::-webkit-scrollbar-thumb {
                    background: transparent;
                    border-radius: 999px;
                    transition: background 320ms ease;
                }
                [data-testid="sc-preview-modal"] .sayz-scroll-premium:hover::-webkit-scrollbar-thumb {
                    background: color-mix(in srgb, var(--sayz-scroll-accent, #2B2A27) 22%, transparent);
                }
                [data-testid="sc-preview-modal"] .sayz-scroll-premium::-webkit-scrollbar-thumb:hover,
                [data-testid="sc-preview-modal"] .sayz-scroll-premium::-webkit-scrollbar-thumb:active {
                    background: color-mix(in srgb, var(--sayz-scroll-accent, #2B2A27) 42%, transparent);
                }
                @media (prefers-reduced-motion: reduce) {
                    [data-testid="sc-preview-modal"] .sayz-scroll-premium { scroll-behavior: auto; }
                }

                /* ─── Mobile tightening (≤480px) ───────────────────────
                   Modal'ın masaüstü ferahlığı aynı kalır, mobile'da aynı
                   editorial dilini daha minimal px'lerle yaşatır:
                     • Shell padding: py-5 px-6 (20/24) → 14/14
                     • Header/footer başlıkları hafif küçülür
                     • Satır gap'ları, iç margin'ler sıkılır
                   Scoped: yalnızca sc-preview-modal + mobile viewport.
                */
                @media (max-width: 480px) {
                    .sayz-sc-shell {
                        padding: 14px !important;
                    }
                    .sayz-sc-shell [data-testid="sc-preview-table"] {
                        font-size: 11.5px !important;
                    }
                    /* Hücre yatay padding mobile'da: 16px (sol-sağ eşit). */
                    .sayz-sc-shell [data-testid="sc-preview-table"] th,
                    .sayz-sc-shell [data-testid="sc-preview-table"] td {
                        padding-left: 16px !important;
                        padding-right: 16px !important;
                    }
                    .sayz-sc-shell [data-testid="sc-preview-tabs"] [class*="px-5"] {
                        padding-left: 12px !important;
                        padding-right: 12px !important;
                    }
                    .sayz-sc-shell [data-testid="sc-preview-cross-cta"] {
                        padding: 12px !important;
                    }
                    /* "Nasıl ölçülür" + "Kalıp/Model" chip grupları mobile'da
                       inline doluluktan column'a düşsün, chip gap sıkılsın. */
                    .sayz-sc-shell [data-testid="sc-preview-howto"] {
                        gap: 8px !important;
                    }
                }

                /* ─── Horizontal table scroll affordance ───────────────
                   Tablo wrapper'ı dar ekranda yatay kayarken, kullanıcıya
                   kaydırılabilir olduğunu belli etmek için ince premium
                   scrollbar (vertical ile aynı stil DNA, 3px, accent). */
                [data-testid="sc-preview-modal"] .sayz-table-x-scroll {
                    -webkit-overflow-scrolling: touch;
                    overscroll-behavior-x: contain;
                    scrollbar-width: thin;
                    scrollbar-color: color-mix(in srgb, var(--sayz-scroll-accent, #2B2A27) 32%, transparent) transparent;
                }
                [data-testid="sc-preview-modal"] .sayz-table-x-scroll::-webkit-scrollbar {
                    height: 4px;
                }
                [data-testid="sc-preview-modal"] .sayz-table-x-scroll::-webkit-scrollbar-track {
                    background: transparent;
                }
                [data-testid="sc-preview-modal"] .sayz-table-x-scroll::-webkit-scrollbar-thumb {
                    background: color-mix(in srgb, var(--sayz-scroll-accent, #2B2A27) 28%, transparent);
                    border-radius: 999px;
                }
                [data-testid="sc-preview-modal"] .sayz-table-x-scroll::-webkit-scrollbar-thumb:hover {
                    background: color-mix(in srgb, var(--sayz-scroll-accent, #2B2A27) 48%, transparent);
                }
            `}</style>

            {/* EDITORIAL — italyan lüks */}
            {isEditorial && (
                <style>{`
                    [data-testid="sc-preview-modal"][data-editorial="true"] button,
                    [data-testid="sc-preview-modal"][data-editorial="true"] input,
                    [data-testid="sc-preview-modal"][data-editorial="true"] textarea,
                    [data-testid="sc-preview-modal"][data-editorial="true"] select,
                    [data-testid="sc-preview-modal"][data-editorial="true"] [role="tab"],
                    [data-testid="sc-preview-modal"][data-editorial="true"] [role="tablist"],
                    [data-testid="sc-preview-modal"][data-editorial="true"] [class*="rounded-sm"],
                    [data-testid="sc-preview-modal"][data-editorial="true"] [class*="rounded-md"],
                    [data-testid="sc-preview-modal"][data-editorial="true"] [class*="rounded-lg"],
                    [data-testid="sc-preview-modal"][data-editorial="true"] [class*="rounded-xl"],
                    [data-testid="sc-preview-modal"][data-editorial="true"] [class*="rounded-2xl"],
                    [data-testid="sc-preview-modal"][data-editorial="true"] [class*="rounded-3xl"] {
                        border-radius: 0 !important;
                    }
                    [data-testid="sc-preview-modal"][data-editorial="true"] [class*="font-bold"],
                    [data-testid="sc-preview-modal"][data-editorial="true"] [class*="font-semibold"] {
                        font-weight: 500 !important;
                    }
                    [data-testid="sc-preview-modal"][data-editorial="true"] [class*="uppercase"] {
                        font-weight: 400 !important;
                        letter-spacing: 0.14em !important;
                    }
                    [data-testid="sc-preview-modal"][data-editorial="true"] [class*="tabular-nums"] {
                        font-weight: 300 !important;
                        letter-spacing: 0.02em !important;
                    }
                `}</style>
            )}

            {/* BOLD — Field Notes "lite": radius 0 KORUNDU, border'lar hafif,
                hero'lar 700 (800'den düştü), uppercase + tabular-nums KORUNDU, 
                tablo grid 1px color-mix %40 opacity (önceden 1.5px %60 koyuydu). */}
            {isBold && (
                <style>{`
                    [data-testid="sc-preview-modal"][data-bold="true"] button,
                    [data-testid="sc-preview-modal"][data-bold="true"] input,
                    [data-testid="sc-preview-modal"][data-bold="true"] textarea,
                    [data-testid="sc-preview-modal"][data-bold="true"] select,
                    [data-testid="sc-preview-modal"][data-bold="true"] [role="tab"],
                    [data-testid="sc-preview-modal"][data-bold="true"] [role="tablist"],
                    [data-testid="sc-preview-modal"][data-bold="true"] [class*="rounded-sm"],
                    [data-testid="sc-preview-modal"][data-bold="true"] [class*="rounded-md"],
                    [data-testid="sc-preview-modal"][data-bold="true"] [class*="rounded-lg"],
                    [data-testid="sc-preview-modal"][data-bold="true"] [class*="rounded-xl"],
                    [data-testid="sc-preview-modal"][data-bold="true"] [class*="rounded-2xl"],
                    [data-testid="sc-preview-modal"][data-bold="true"] [class*="rounded-3xl"] {
                        border-radius: 0 !important;
                    }
                    /* İç butonlar/inputlar 1px (nefes al) + hover/focus 1.5px */
                    [data-testid="sc-preview-modal"][data-bold="true"] button[class*="border"],
                    [data-testid="sc-preview-modal"][data-bold="true"] input[class*="border"],
                    [data-testid="sc-preview-modal"][data-bold="true"] select[class*="border"] {
                        border-width: 1px !important;
                    }
                    [data-testid="sc-preview-modal"][data-bold="true"] button[class*="border"]:hover,
                    [data-testid="sc-preview-modal"][data-bold="true"] button[class*="border-foreground"],
                    [data-testid="sc-preview-modal"][data-bold="true"] input[class*="border"]:focus {
                        border-width: 1.5px !important;
                    }
                    [data-testid="sc-preview-modal"][data-bold="true"] [class*="font-bold"] {
                        font-weight: 700 !important;
                    }
                    [data-testid="sc-preview-modal"][data-bold="true"] [class*="font-semibold"] {
                        font-weight: 650 !important;
                    }
                    [data-testid="sc-preview-modal"][data-bold="true"] [class*="uppercase"] {
                        font-weight: 700 !important;
                        letter-spacing: 0.04em !important;
                    }
                    [data-testid="sc-preview-modal"][data-bold="true"] [class*="tabular-nums"] {
                        font-weight: 600 !important;
                    }
                    /* Tablo border kuralı KALDIRILDI — sade şablonun default minimal
                       border-border/40 render'ı Bold'da da aynen korunur. Önceki kural
                       her th/td'nin 4 kenarına koyu %40 border veriyordu → çok kabaydı. */
                `}</style>
            )}
            {/* Header — SC "beden tablosu" başlığı FMS modal'ındaki ile aynı stil.
                `-mx-6 -mt-5 px-6 pt-5 pb-5` numarası ile border-b, modal shell'in
                yatay kenarlarına yapışır (parent'ın px-6'sını undo eder, py-5
                top padding'ini undo eder). FMS modal'ıyla paralel px-6 (24px)
                horizontal rhythm. */}
            <div className="-mx-6 -mt-5 px-6 pt-5 pb-5 border-b border-border/60 shrink-0">
                <div className="flex items-center justify-between gap-3">
                    <div className="text-[15px] font-semibold truncate tracking-tight text-foreground" data-testid="sc-preview-title">
                        {modalTitle}
                    </div>
                    <button
                        type="button"
                        aria-label="Kapat"
                        className="shrink-0 h-7 w-7 grid place-items-center rounded-md text-muted-foreground/50 hover:text-foreground hover:bg-muted/50 transition-colors"
                        onClick={() => {
                            // Prefer the explicit onClose prop (Shadow DOM widget);
                            // fall back to postMessage for legacy iframe embeds.
                            // No-op in the admin panel preview.
                            if (typeof onClose === "function") {
                                onClose();
                                return;
                            }
                            try {
                                if (typeof window !== "undefined" && window.parent && window.parent !== window) {
                                    window.parent.postMessage({ sayz: true, type: "close" }, "*");
                                }
                            } catch (_) { /* noop */ }
                        }}
                        data-testid="sc-close-btn"
                    >
                        <X className="h-[18px] w-[18px]" strokeWidth={1.5} />
                    </button>
                </div>
            </div>

            {/* Body — ultra-slim 3px webkit scrollbar overlay sağda. Firefox'ta
                scrollbar gizli (scrollbar-width: none). Hem desktop hem Shadow
                DOM Chrome tabanlı, bu yüzden overlay davranışı tüm merchant
                sitelerinde aynı. px-6 → header & footer'daki px-6 ile birebir
                hizalı, scrollbar-gutter rezervi yok.
                `overflow-x-hidden` kritik: içerideki tablolar `overflow-x-auto`
                sarmalı sayesinde kendi kendilerine yatay kaydırıyor; body'nin
                kendisi x-overflow görürse sağdaki px-6 padding görünmez olur
                (içerik shell sağ kenarına yapışır) — mobil hata buydu. */}
            <div
                ref={scrollRef}
                className="sayz-scroll-premium relative flex-1 min-h-0 overflow-y-auto overflow-x-hidden -mx-6 py-5 px-6 space-y-4"
                data-testid="sc-preview-scroll"
            >
                {inlineBlocks.map((blockId) => (
                    <div key={blockId}>
                        <BlockRenderer
                            blockId={blockId}
                            settings={settings}
                            accent={accent}
                            sample={sample}
                            findMySizeCta={findMySizeCta}
                            bodyImage={bodyImage}
                            previewGuides={previewGuides}
                            widgetTemplate={widgetTemplate}
                            widgetTokens={widgetTokens}
                            onSwitchToFindMySize={onSwitchToFindMySize}
                        />
                    </div>
                ))}
            </div>

            {/* Sticky cross-CTA footer — if the merchant has enabled the
                "Bedenini bulamadın mı?" block, we render it here (outside the
                scroll container) so the primary conversion action stays
                anchored to the bottom of the modal. The block's position in
                `blockOrder` becomes a toggle rather than an ordering input,
                which is the right UX trade-off for a conversion CTA. */}
            {hasFooterCta && (
                <div
                    className="-mx-6 -mb-5 px-6 pt-5 pb-5 border-t border-border/60 shrink-0 bg-surface"
                    data-testid="sc-preview-footer"
                >
                    <CrossCta
                        text={settings.crossCtaText || DEFAULT_CROSS_CTA_TEXT}
                        desc={settings.crossCtaDesc || DEFAULT_CROSS_CTA_DESC}
                        ctaLabel={findMySizeCta}
                        widgetTemplate={widgetTemplate}
                        widgetTokens={widgetTokens}
                        onCtaClick={onSwitchToFindMySize}
                    />
                </div>
            )}
        </div>
    );
};

const BlockRenderer = ({ blockId, settings, accent, sample, findMySizeCta, bodyImage, previewGuides, widgetTemplate, widgetTokens, onSwitchToFindMySize }) => {
    switch (blockId) {
        case "image":
            return <ImageBlock bodyImage={bodyImage} sample={sample} />;
        case "table":
            return settings.displayType === "table" ? (
                <StandardTableView
                    sample={sample}
                    accent={accent}
                    showTableName={settings.showTableName}
                />
            ) : (
                <TabsTableView
                    sample={sample}
                    accent={accent}
                    showTableName={settings.showTableName}
                />
            );
        case "how-to-measure":
            return <HowToMeasure accent={accent} previewGuides={previewGuides} />;
        case "cross-cta":
            return (
                <CrossCta
                    text={settings.crossCtaText || DEFAULT_CROSS_CTA_TEXT}
                    desc={settings.crossCtaDesc || DEFAULT_CROSS_CTA_DESC}
                    ctaLabel={findMySizeCta}
                    widgetTemplate={widgetTemplate}
                    widgetTokens={widgetTokens}
                    onCtaClick={onSwitchToFindMySize}
                />
            );
        default:
            return null;
    }
};

/**
 * Görsel bloğu — Görseller sayfasındaki "body size" görselini kullanır.
 *
 * Görsel oranı belirsiz (kare / yatay dikdörtgen / dikey dikdörtgen olabilir).
 * Çözüm: aspect-[3/2] sabit bir kutu içinde iki katman —
 *   1) `object-cover + blur + scale` ile aynı görsel arka plan (netflix stili
 *      blur backdrop) — boşlukları temiz doldurur, görselin baskın rengi
 *      atmosfer olarak taşınır
 *   2) `object-contain` ile görselin tam hali üstte — kırpılma yok, oran
 *      korunur
 * Sonuç: dikey/yatay/kare fark etmez, kutu her zaman dolu ve görsel bozulmaz.
 */
const ImageBlock = ({ bodyImage, sample }) => {
    const [failed, setFailed] = useState(false);
    const src = resolveBodyImageSrc(bodyImage);
    // Treat 1×1 placeholder PNGs the same as a missing image. The backend
    // occasionally serves a stub when storage is bootstrapping or an upload
    // was never completed — showing a stretched 1px bar is worse than hiding.
    const onImgLoad = (e) => {
        const img = e?.currentTarget;
        if (img && img.naturalWidth < 16 && img.naturalHeight < 16) setFailed(true);
    };
    if (src && !failed) {
        return (
            <div
                className="relative rounded-md w-full aspect-[4/3] overflow-hidden border border-border bg-surface"
                data-testid="sc-preview-image"
            >
                <img
                    src={src}
                    alt=""
                    aria-hidden="true"
                    className="absolute inset-0 w-full h-full object-cover scale-110 blur-lg opacity-70"
                    loading="lazy"
                    onError={() => setFailed(true)}
                    onLoad={onImgLoad}
                />
                <img
                    src={src}
                    alt={bodyImage.alt || bodyImage.name || "Beden ölçü diyagramı"}
                    className="relative w-full h-full object-contain"
                    loading="lazy"
                    onError={() => setFailed(true)}
                    onLoad={onImgLoad}
                />
            </div>
        );
    }
    // No valid image (never configured OR fetch failed OR broken stub) —
    // hide the block entirely in the live widget so merchants never see a
    // stretched 1px bar or empty gradient placeholder. In the admin panel
    // preview we show a subtle Ruler-icon placeholder so merchants see where
    // the image would appear once they upload one.
    if (failed) return null;
    return (
        <div
            className="rounded-md w-full grid place-items-center border border-border aspect-[4/3]"
            style={{ background: sample.productImageBg }}
            data-testid="sc-preview-image"
        >
            <Ruler className="h-8 w-8 text-white/60" strokeWidth={1} />
        </div>
    );
};

/**
 * Cross-CTA — Sayz Widget "ActionRow2Col primary" birebir:
 *  bg: color-mix(accent 10%), border: 0.5px accent, text/icon/arrow: accent
 */
/**
 * CROSS-CTA — Beden Tablosu içindeki "Bedenimi Bul" köprü butonu.
 * CTA'nın tasarım kaynağı: SayzCta component (Sayz Widget sayfası).
 * Buradaki widgetTemplate + widgetTokens Sayz Widget config'inden hydrate
 * edilir; merchant Sayz Widget'ta accent/radius/spacing değiştirdiğinde bu
 * CTA da otomatik güncellenir.
 */
const CrossCta = ({ text, desc, ctaLabel, widgetTemplate, widgetTokens, onCtaClick }) => (
    <div
        className="sayz-cross-cta flex items-center justify-between gap-4 rounded-md px-4 py-3"
        style={{ background: "hsl(var(--muted) / 0.55)" }}
        data-testid="sc-preview-cross-cta"
    >
        {/* Mobile'da (≤480px) yazı + buton üstlü-altlı. Buton tam genişlik
            olsun ki parmak hedefi netleşsin; text tek satırda sığmazsa
            doğal wrap (truncate kaldırıldı — mobile'da yazı kesilmez).
            Scoped: `.sayz-cross-cta` prefix'i widget shadow DOM'una özel. */}
        <style>{`
            @media (max-width: 480px) {
                .sayz-cross-cta {
                    flex-direction: column !important;
                    align-items: stretch !important;
                    gap: 10px !important;
                    padding: 12px !important;
                }
                .sayz-cross-cta [data-slot="cross-cta-text"] {
                    text-align: left;
                }
                .sayz-cross-cta [data-slot="cross-cta-text"] > div:first-child {
                    white-space: normal !important;
                    overflow: visible !important;
                    text-overflow: clip !important;
                }
                .sayz-cross-cta [data-testid="sc-preview-cross-cta-btn"] {
                    width: 100% !important;
                    justify-content: center !important;
                }
            }
        `}</style>
        <div className="min-w-0 flex-1" data-slot="cross-cta-text">
            <div className="text-[13px] font-semibold text-foreground truncate">
                {text}
            </div>
            <div className={`${WIDGET_TYPO_CLS.body} text-muted-foreground leading-relaxed mt-0.5`}>
                {desc}
            </div>
        </div>
        <SayzCta
            label={ctaLabel}
            icon={Sparkles}
            variant="primary"
            template={widgetTemplate}
            tokens={widgetTokens}
            testId="sc-preview-cross-cta-btn"
            onClick={onCtaClick}
        />
    </div>
);

/* ═══════════════════════════════════════════════════════════════
 * DISPLAY VIEWS
 * ═══════════════════════════════════════════════════════════════ */

/**
 * Standart Tablo — Excel/Sheets tarzı cross-highlight hover:
 *  · Hover'daki hücre: foreground bg + background text (zıt renk)
 *  · Aynı satır ve sütundaki hücreler: accent %10 opacity bg (cross)
 *  · Zebra: tek satırlar beyaz, çift satırlar hafif gri
 *  · Header/Label cells: bg-muted vurgu; hover'da aynı davranış
 * Row index -1 = header, 0..N = body. Col index -1 = label, 0..M = size.
 */
const StandardTableView = ({ sample, accent, showTableName }) => {
    const [active, setActive] = useState({ r: null, c: null });

    const clear = () => setActive({ r: null, c: null });
    const set = (r, c) => setActive({ r, c });

    const cellStyle = (r, c, opts = {}) => {
        const hasActive = active.r !== null && active.c !== null;
        const isActive = hasActive && active.r === r && active.c === c;
        const inRow = active.r !== null && active.r === r;
        const inCol = active.c !== null && active.c === c;

        if (isActive) {
            return {
                background: accent,
                color: "#fff",
            };
        }
        if (inRow || inCol) {
            return {
                background: `color-mix(in srgb, ${accent} 10%, transparent)`,
            };
        }
        // Default: zebra pattern (body rows) or header bg
        if (opts.header || opts.label) {
            return { background: "hsl(var(--muted) / 0.55)" };
        }
        return {
            background: opts.zebra ? "hsl(var(--muted) / 0.28)" : "transparent",
        };
    };

    const baseCellCls =
        "px-5 py-2.5 text-[12px] font-medium tabular-nums transition-colors duration-150";

    return (
        <div
            className="rounded-md border border-border overflow-hidden"
            data-testid="sc-preview-table"
            onMouseLeave={clear}
        >
            {showTableName && sample.name && (
                <div
                    className="px-5 py-2.5 border-b border-border/60 text-[12.5px] font-semibold text-foreground tracking-tight"
                    style={{ background: "hsl(var(--muted) / 0.35)" }}
                    data-testid="sc-preview-table-name"
                >
                    {sample.name}
                </div>
            )}
            {/* Horizontal scroll wrapper — dar ekranlarda "Ön Boy" gibi iki
                kelimeli etiketler alt satıra düşmek yerine, tablo kendi
                içinde yatay kayar. `whitespace-nowrap` tüm hücrelerde text
                wrap'ı kapatır, `w-max` tabloya içeriği kadar genişleme
                izni verir, parent `overflow-x-auto` scroll barını tetikler. */}
            <div className="w-full overflow-x-auto sayz-table-x-scroll" data-testid="sc-preview-table-scroll">
                <table className="w-max min-w-full border-collapse text-[12px] whitespace-nowrap">
                    <thead>
                        <tr className="border-b border-border/40">
                            <th
                                className={`text-left ${baseCellCls}`}
                                style={cellStyle(-1, -1, { header: true, label: true })}
                                onMouseEnter={() => set(-1, -1)}
                            >
                                <span
                                    className="text-[10.5px] font-semibold uppercase tracking-wider transition-colors duration-150"
                                    style={{
                                        color:
                                            active.r === -1 && active.c === -1
                                                ? "rgba(255,255,255,0.85)"
                                                : "hsl(var(--muted-foreground))",
                                    }}
                                >
                                    Ölçü (cm)
                                </span>
                            </th>
                            {sample.sizes.map((s, ci) => (
                                <th
                                    key={s}
                                    className={`text-center font-semibold ${baseCellCls}`}
                                    style={{
                                        ...cellStyle(-1, ci, { header: true }),
                                        color: active.r === -1 && active.c === ci ? "#fff" : "hsl(var(--foreground))",
                                    }}
                                    onMouseEnter={() => set(-1, ci)}
                                    data-testid={`sc-preview-th-${s}`}
                                >
                                    {s}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {sample.rows.map((row, ri) => (
                            <tr
                                key={row.label}
                                className={ri < sample.rows.length - 1 ? "border-b border-border/40" : ""}
                            >
                                <td
                                    className={`text-left text-muted-foreground ${baseCellCls}`}
                                    style={cellStyle(ri, -1, { label: true })}
                                    onMouseEnter={() => set(ri, -1)}
                                    data-testid={`sc-preview-row-${row.label}`}
                                >
                                    {row.label}
                                </td>
                                {row.values.map((v, ci) => (
                                    <td
                                        key={ci}
                                        className={`text-center ${baseCellCls}`}
                                        style={cellStyle(ri, ci, { zebra: ri % 2 === 1 })}
                                        onMouseEnter={() => set(ri, ci)}
                                        data-testid={`sc-preview-cell-${ri}-${ci}`}
                                    >
                                        {v}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

/**
 * Sekmeli Tablo — Segmented control chip strip + focal header + hover-lit değer listesi.
 * Standart Tablo ile aynı DNA: border + rounded + zebra + accent hover.
 */
const TabsTableView = ({ sample, accent, showTableName }) => {
    const [activeIdx, setActiveIdx] = useState(0);
    const [hoverTabIdx, setHoverTabIdx] = useState(null);
    const [hoverRow, setHoverRow] = useState(null);

    return (
        <div className="rounded-md border border-border overflow-hidden" data-testid="sc-preview-tabs">
            {showTableName && sample.name && (
                <div
                    className="px-5 py-2.5 border-b border-border/60 text-[12.5px] font-semibold text-foreground tracking-tight"
                    style={{ background: "hsl(var(--muted) / 0.35)" }}
                    data-testid="sc-preview-tabs-name"
                >
                    {sample.name}
                </div>
            )}
            {/* Segmented control chip strip — outer container'ın bir parçası */}
            <div
                className="p-1.5 flex gap-1 border-b border-border/40"
                style={{ background: "hsl(var(--muted) / 0.28)" }}
            >
                {sample.sizes.map((s, i) => {
                    const active = i === activeIdx;
                    const hover = hoverTabIdx === i;
                    const bg = active
                        ? accent
                        : hover
                        ? `color-mix(in srgb, ${accent} 12%, transparent)`
                        : "transparent";
                    return (
                        <button
                            key={s}
                            type="button"
                            onClick={() => setActiveIdx(i)}
                            onMouseEnter={() => setHoverTabIdx(i)}
                            onMouseLeave={() => setHoverTabIdx(null)}
                            className="flex-1 h-8 px-3 text-[12px] font-semibold tracking-wide rounded transition-all tabular-nums"
                            style={{
                                background: bg,
                                color: active ? "#fff" : "hsl(var(--foreground))",
                                boxShadow: active
                                    ? `0 1px 2px color-mix(in srgb, ${accent} 35%, transparent)`
                                    : "none",
                            }}
                            data-testid={`sc-preview-tab-${s}`}
                            aria-pressed={active}
                        >
                            {s}
                        </button>
                    );
                })}
            </div>

            {/* Focal header — BEDEN solda, ÖLÇÜ (CM) sağda */}
            <div
                className="flex items-baseline justify-between gap-3 px-5 py-2.5 border-b border-border/40"
                style={{ background: "hsl(var(--muted) / 0.55)" }}
            >
                <span className="text-[10.5px] font-semibold uppercase tracking-wider text-muted-foreground whitespace-nowrap">
                    Beden
                </span>
                <span className="text-[10.5px] font-semibold uppercase tracking-wider text-muted-foreground whitespace-nowrap">
                    Ölçü (cm)
                </span>
            </div>

            {sample.rows.map((row, i) => {
                const isLast = i === sample.rows.length - 1;
                const isHover = hoverRow === i;
                const bg = isHover
                    ? `color-mix(in srgb, ${accent} 10%, transparent)`
                    : i % 2 === 1
                    ? "hsl(var(--muted) / 0.28)"
                    : "transparent";
                return (
                    <div
                        key={row.label}
                        onMouseEnter={() => setHoverRow(i)}
                        onMouseLeave={() => setHoverRow(null)}
                        className={`flex items-baseline justify-between gap-3 px-5 py-2.5 transition-colors duration-150 ${
                            !isLast ? "border-b border-border/40" : ""
                        }`}
                        style={{ background: bg }}
                        data-testid={`sc-preview-tabs-row-${row.label}`}
                    >
                        <span className="text-[12px] text-muted-foreground font-medium whitespace-nowrap">
                            {row.label}
                        </span>
                        <span
                            className="text-[12px] font-medium tabular-nums transition-colors duration-150 whitespace-nowrap"
                            style={{
                                color: isHover ? accent : "hsl(var(--foreground))",
                            }}
                        >
                            {row.values[activeIdx]}
                        </span>
                    </div>
                );
            })}
        </div>
    );
};

const HowToMeasure = ({ accent, previewGuides }) => {
    const guides = previewGuides && previewGuides.length > 0 ? previewGuides : null;

    // Empty state — hiçbir ölçü grubunda henüz "Nasıl Ölçülür" açıklaması yok
    if (!guides) {
        return (
            <div data-testid="sc-preview-htm-empty">
                <div className="flex items-start gap-2">
                    <span
                        className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full"
                        style={{ background: accent, color: "#fff" }}
                        aria-hidden="true"
                    >
                        <Ruler className="h-3 w-3" strokeWidth={1.75} />
                    </span>
                    <div className="min-w-0 flex-1 pt-0.5">
                        <div
                            className="text-[11px] font-semibold uppercase tracking-wider leading-tight"
                            style={{ color: accent }}
                        >
                            Nasıl Ölçülür
                        </div>
                        <div className="text-[12px] text-muted-foreground leading-6">
                            Ölçü Grupları sayfasından her ölçüye kısa bir &ldquo;Nasıl Ölçülür&rdquo; açıklaması ekle — burada rehber olarak görünecek.
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-3" data-testid="sc-preview-htm">
            <div className="flex items-start gap-2">
                <span
                    className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full"
                    style={{
                        background: accent,
                        color: "#fff",
                    }}
                    aria-hidden="true"
                >
                    <Ruler className="h-3 w-3" strokeWidth={1.75} />
                </span>
                <div className="min-w-0 flex-1 pt-0.5">
                    <div
                        className="text-[11px] font-semibold uppercase tracking-wider leading-tight"
                        style={{ color: accent }}
                    >
                        Nasıl Ölçülür
                    </div>
                    <div className="text-[12px] text-muted-foreground leading-6">
                        Doğru bedeni bulmak için ölçülerini bir mezura ile aşağıdaki yönergelere göre al.
                    </div>
                </div>
            </div>
            <ol className="space-y-3">
                {guides.map((g, idx) => (
                    <li key={g.key} className="flex items-start gap-2">
                        <span
                            className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[10px] font-semibold tabular-nums tracking-tight"
                            style={{
                                background: `color-mix(in srgb, ${accent} 10%, transparent)`,
                                color: accent,
                            }}
                            aria-hidden="true"
                        >
                            {String(idx + 1).padStart(2, "0")}
                        </span>
                        <div className="min-w-0 flex-1 pt-0.5">
                            <div className="text-[12.5px] font-semibold text-foreground leading-tight">
                                {g.label}
                            </div>
                            <div className="text-[12px] text-muted-foreground leading-6">
                                {g.hint}
                            </div>
                        </div>
                    </li>
                ))}
            </ol>
        </div>
    );
};

// Named export for live-embed widget (shared source of truth with panel preview)
export { PreviewCanvas as SizeChartPreviewCanvas };
